<template>
	<b-container fluid class="video-container">
		<b-row no-gutters>
			<b-col xl="8" offset-xl="2" lg="10" offset-lg="1" class="video-body">
				<!-- Header -->
				<rb-collapse :collapsed="!loaded">
					<b-container fluid v-if="video" class="my-3">
						<b-row class="my-3">
							<b-col md="12">
								<h2>
									<span class="text-right float-right d-none d-sm-block">
										{{ video.Views }} <rb-icon>eye</rb-icon>
									</span>

									{{ video.Name }}
								</h2>

								<h6 class="float-right text-grey">
									{{ video.Date.format('d/m/Y') }}
								</h6>

								<h4 class="mb-3">
									<router-link :to="video.Category.Series.getRoute()">
										{{ video.Category.Series.Name }}
									</router-link>
									-
									<router-link :to="video.Category.getRoute()">
										{{ video.Category.Name }}
									</router-link>
								</h4>

								<rb-markdown :value="video.Description"></rb-markdown>

								<p class="mt-3 text-center">

									<a
										v-for="source in video.Sources"
										:key="source.quality.size"
										:href="video.getUrl(source)"
										target="_blank"
										class="d-inline-block mr-5">
										<rb-icon>file-download</rb-icon>

										<b>
											{{source.quality.name}}
										</b>

										{{source.quality.size}}p
									</a>

									<a
										:href="video.Subtitle"
										target="_blank"
										class="d-inline-block mr-5">
										<rb-icon>file-download</rb-icon>

										<b>
											Legenda
										</b>

										.ass
									</a>

								</p>
							</b-col>
						</b-row>
					</b-container>
				</rb-collapse>

				<!-- Donnation CTA -->
				<rb-collapse :collapsed="!loaded || !loggedUser || (!!loggedUser && !!loggedUser.Status)">
					<rb-alert class="mb-4">
						<template v-if="timeUntilAvailable">

							<b>Quer ver agora?</b> Torne-se já um doador!
							<router-link :to="{ name: `account` }">
								Clique aqui!
							</router-link>

						</template>

						<template v-else>

							<b>Quer ver em FullHD?</b> Torne-se já um doador!
							<router-link :to="{ name: `account` }">
								Clique aqui!
							</router-link>

						</template>
					</rb-alert>
				</rb-collapse>

				<rb-video-player
					:user="loggedUser"
					:sources="video ? video.Sources : []"
					:poster="video ? video.Thumbnail : ''"
					:cues="video ? [video.Data.cues.opEnd, video.Data.cues.edStart].filter(x => x.position) : []"
					:metaData="video ? {
						title: video.Name,
						artist: video.Category.Series.Name,
						album: video.Category.Name,
					} : {}"

					:opening-end="video && video.Data.cues.opEnd.position ? video.Data.cues.opEnd : null"
					:ending-start="video && video.Data.cues.edStart.position ? video.Data.cues.edStart : null"

					:has-next-video="videoIndex < videos.length-1"
					:auto-play="autoPlayVideo"

					@play="onVideoPlay"
					@next="nextVideo"

					ref="player"
				>
					<!-- Blocking Banners -->
					<div class="countdown-banner py-5" v-if="timeUntilAvailable">
						<h3 v-if="loggedUser && loggedUser.Status">
							Este vídeo será liberado para FIRSTs/Doadores em:
						</h3>
						<h3 v-else>
							Este vídeo será liberado para o público em:
						</h3>
						<h3>{{timeUntilAvailableString}}</h3>
					</div>

					<div class="countdown-banner py-5" v-if="loaded && !loggedUser && !timeUntilAvailable">
						<h3>
							Agora é necessário <a class="text-bold" :href="loginUrl">fazer login</a> antes de assistir os videos!
						</h3>
					</div>

					<!-- Video Cue Management -->
					<transition name="fade">
						<div
							v-if="video && cuesUiActive"
							class="cues-ui"
							@click.stop.prevent
						>
							<div class="cue-data">
								<b>Op End</b> - <i>W</i>
								<input type="number" step=".1" v-model="video.Data.cues.opEnd.position">
							</div>
							<div class="cue-data">
								<b>Ed Start</b> - <i>E</i>
								<input type="number" step=".1" v-model="video.Data.cues.edStart.position">
							</div>
						</div>
					</transition>
				</rb-video-player>

				<div class="text-right mt-4">
					<span
						class="btn-autoplay"
						:class="{active: VideoSettings.autoPlay}"
						@click.prevent="VideoSettings.autoPlay = !VideoSettings.autoPlay"
						v-b-tooltip.hover
						title="Auto play"
					>
						<rb-icon>redo-alt</rb-icon>
					</span>
				</div>

				<!-- Carousel -->
				<rb-collapse :collapsed="!loadedVideos">
					<rb-horizontal-scroll class="playlist my-4" :speed="360" :focus-item="videoIndex">
						<rb-video-thumbnail
							v-for="v in videos" :key="v.Id"
							:video="v"
							:active="v.Id !== video.Id"
							small
						/>
					</rb-horizontal-scroll>
				</rb-collapse>

				<!-- Comments -->
				<rb-collapse :collapsed="!loaded">
					<div class="mt-5 mb-5" v-if="video">
						<rb-button
							class="float-right" variant="info" size="sm"
							@click="showSpoilers = !showSpoilers"
						>
							<rb-icon key="1" v-if="showSpoilers">eye-slash</rb-icon>
							<rb-icon key="2" v-else>eye</rb-icon>
							{{ showSpoilers ? 'Esconder spoilers' : 'Mostrar spoilers' }}
						</rb-button>

						<h3>
							Comentários
							<template v-if="allComments.length">({{allComments.length}})</template>
						</h3>

						<div class="new-comment mt-5" v-if="loggedUser">
							<h4>
								Faça um comentário
							</h4>

							<rb-comment-composer
								:video="video"
							></rb-comment-composer>
						</div>

						<template
							v-for="comment in comments"
							:comment="comment"
						>
							<rb-comment
								:video="video"
								:comment="comment"
								:showSpoilers="showSpoilers"

								:key="comment.Id"
							></rb-comment>
						</template>

						<div class="text-center" v-if="allComments.length > maxComments">
							<rb-button variant="primary" size="sm" @click="seeMoreComments">
								Ver mais comentários
							</rb-button>
						</div>
					</div>
				</rb-collapse>
			</b-col>
		</b-row>
	</b-container>
</template>

<script>
	import RbVideoPlayer from '../components/RbVideoPlayer/RbVideoPlayer';
	import RbCollapse from '../components/RbCollapse';
	import RbVideoThumbnail from '../components/RbVideoThumbnail';
	import RbHorizontalScroll from '../components/RbHorizontalScroll';
	import {VideoSettings} from '../components/RbVideoPlayer/VideoSettings';
	import {mapState} from 'vuex';
	import RbMarkdown from '../components/RbMarkdown';
	import RbAlert from '../components/RbAlert';
	import {oldApi} from '../app/oldApi';
	import RbComment from '../components/Comments/RbComment';
	import RbCommentComposer from '../components/Comments/RbCommentComposer';
	import {Keys} from '../../../shared/util/Keys';
	import RbButton from '../components/RbButton';

	export default {
		name: 'Video',
		components: {
			RbButton,
			RbCommentComposer,
			RbComment,
			RbAlert,
			RbMarkdown,
			RbHorizontalScroll,
			RbVideoThumbnail,
			RbCollapse,
			RbVideoPlayer
		},
		metaInfo (){
			return {
				title: this.video ? this.video.Name : 'Vïdeo'
			}
		},
		data: () => ({
			video: null,
			videoHasBeenPlayed: false,
			category: null,
			videos: [],

			loaded: false,
			loadedVideos: false,
			autoPlayVideo: false,

			loginUrl: oldApi.getUrl({
				url: 'Auth.FacebookLogin',
				params: {
					'origin-url': 'h'+window.location
				}
			}),

			VideoSettings,

			now: Date.now(),

			showSpoilers: false,

			cuesUiActive: false,

			maxComments: 20
		}),
		watch: {
			'$route': async function () {
				this.loadData();
			}
		},
		async mounted() {
			this.loadData();

			window.addEventListener('keydown', this.onKeyDown);

			if (this.$route.hash.length > 1) {
				this.watchPartyActive = true;
			}
		},
		beforeDestroy() {
			window.removeEventListener('keydown', this.onKeyDown);
		},
		methods: {
			async loadData() {
				const id = this.$route.params.id || this.$route.query.id;

				if (!this.video || this.video.Id !== id) {
					this.video = await this.$oldApi.Video.get(id);

					this.loaded = true;

					this.$router.replace({
						...this.video.getRoute(),
						hash: this.$route.hash
					});

					this.videoHasBeenPlayed = false;
					this.commentText = '';
				}

				if (!this.category || this.category.Id !== this.video.Category.Id) {
					this.category = this.video.Category;
					this.videos = [];
					this.loadedVideos = false;

					this.videos = await this.$oldApi.Video.listByCategory(this.category.Id);

					this.loadedVideos = true;
				}

				this.$nextTick(() => this.autoPlayVideo = false);

				if (this.timeUntilAvailable > 0) {
					this.updateNow();
				}
			},

			nextVideo() {
				this.autoPlayVideo = true;
				this.$router.push(this.videos[this.videoIndex+1].getRoute());
			},

			async onVideoPlay() {
				if (!this.videoHasBeenPlayed) {
					await this.$oldApi.Video.addView(this.video.Id);

					this.video.Views++;

					this.videoHasBeenPlayed = true;

					this.$ga.event({
						eventCategory: 'Video',
						eventAction: 'play',
						eventLabel: this.video.Name
					})
				}
			},

			updateNow() {
				this.now = Date.now();

				if (this.timeUntilAvailable > 0) {
					const currentId = this.video.Id;

					setTimeout(() => {
						if (currentId === this.video.Id) this.updateNow()
					}, 1000);
				} else {
					window.location.reload(true);
				}
			},


			// Cue stuff
			onKeyDown(e) {
				switch (e.which) {
					case Keys.F2:
						this.toggleCues();
						break;
					case Keys.F9:
						this.watchPartyActive = true;
						break;
					case Keys.W:
						this.updateCue(this.video.Data.cues.opEnd);
						break;
					case Keys.E:
						this.updateCue(this.video.Data.cues.edStart);
						break;
					case Keys.S:
						if (this.cuesUiActive) {
							this.saveCues();
						}
						break;
				}
			},

			toggleCues() {
				if (!this.loggedUser.IsAdmin) return;
				this.cuesUiActive = !this.cuesUiActive;
			},
			updateCue(cue) {
				const {video: player} = this.$refs.player;

				cue.position = player.position;
			},
			async saveCues() {
				let r = await oldApi.Video.setCues(this.video);
				alert(JSON.stringify(r));
			},
			seeMoreComments() {
				this.maxComments += 20;
			}
		},
		computed: {
			...mapState([
				'loggedUser'
			]),
			videoIndex() {
				return this.videos.indexOf(this.videos.find(x => x.Id === this.video.Id))
			},
			timeUntilAvailable() {
				if (!this.video) return 0;

				let time = 0;

				if (this.loggedUser && this.loggedUser.Status) {
					time = Math.max(this.video.Date.getTime() - this.now, time);
				} else {
					time = Math.max(this.video.PublicAvailability.getTime() - this.now, time);
				}

				return time;
			},
			timeUntilAvailableString() {
				let time = this.timeUntilAvailable;

				let seconds = Math.floor(time / 1000);
				time -= seconds * 1000;

				let minutes = Math.floor(seconds / 60);
				seconds -= minutes * 60;

				let hours = Math.floor(minutes / 60);
				minutes -= hours * 60;

				let days = Math.floor(hours / 24);
				hours -= days * 24;

				return (days ? days + "d " : '') + hours.pad(2) + ":" + minutes.pad(2) + ":" + seconds.pad(2);
			},
			comments() {
				const isAdmin = (this.loggedUser && this.loggedUser.IsAdmin);

				return (isAdmin ? this.video.Comments : this.video.Comments.filter(c => !c.Deleted)).slice(0, this.maxComments);
			},
			allComments() {
				const isAdmin = (this.loggedUser && this.loggedUser.IsAdmin);

				return isAdmin ? this.video.Comments : this.video.Comments.filter(c => !c.Deleted);
			}
		}
	}
</script>
<style lang="scss" scoped>
	.video-container {
		background: rgba(0, 0, 0, 0.4);
	}

	.video-body {
		overflow: hidden;

		.rb-player {
			box-shadow: 0 0 15px rgba(0, 0, 0, 0.4);
		}

		.playlist {
			.rb-thumbnail {
				width: 350px;

				&.active {
					opacity: .7;
					&:hover {
						opacity: 1;
					}
				}
			}
		}
	}

	.btn-autoplay {
		opacity: .5;
		transition: .3s all;
		cursor: pointer;

		&.active {
			opacity: 1;
		}
	}

	.countdown-banner {
		position: absolute;
		left: 0;
		right: 0;
		top: 50%;

		transform: translateY(-50%);

		background: linear-gradient(to right, rgba(0, 0, 0, .2) 0%, rgba(0, 0, 0, .9) 50%, rgba(0, 0, 0, .2) 100%);

		text-align: center;
		text-shadow: 0 0 5px rgba(0, 0, 0, 0.5);
	}

	.cues-ui {
		position: absolute;
		right: 20px;
		top: 20px;

		background: linear-gradient(to bottom, rgba(0, 0, 0, .4) 0%, rgba(0, 0, 0, .2) 100%);

		display: flex;

		.cue-data {
			width: 120px;
			flex-shrink: 0;

			text-align: center;
			padding: 1em .5em;

			input {
				width: 100%;
				text-align: center;
			}
		}
	}
</style>
